import { Paper, Stack } from '@mantine/core';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { IconArrowLeft, IconFolder } from '@tabler/icons-react';
import { DIVIDER, SidebarItem } from 'src/UI/page-nav/sidebar/types';
import { PageHeader } from 'src/data-capture/file-management/components/PageHeader';
import PageWithSidebar from 'src/UI/page-nav/PageWithSidebar';
import { FileDragDropProvider } from 'src/data-capture/file-management/context/FileDragDropContext';
import { useFileDragDrop } from 'src/data-capture/file-management/hooks/useFileDragDrop';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useCompanyFiles } from '../hooks/useCompanyFiles';
import { useCompanyFileActions } from '../hooks/useCompanyFileActions';

type CompanyFilesLayoutProps = {
  children: React.ReactNode;
  title: string;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchResultCount?: number;
  actions?: React.ReactNode;
};

export default function CompanyFilesLayout({
  children,
  title,
  showSearch,
  searchValue,
  onSearchChange,
  searchResultCount,
  actions,
}: CompanyFilesLayoutProps) {
  const { files, refetchFiles } = useCompanyFiles();
  const { moveFile } = useCompanyFileActions();
  const location = useLocation();

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { fileId: file.id, targetId: parentId },
      );
    }
  };

  const dragDropHook = useFileDragDrop({
    allFiles: files,
    handleMoveFile,
  });

  const folderLinks: SidebarItem = useMemo(() => {
    const FolderIcon = (props: { size?: number; color?: string }) => <IconFolder {...props} />;

    const folders = files.filter(file => file.isFolder);
    const placed = new Set<string>();

    const buildFolderHierarchy = (parentId: string | null): SidebarItem[] =>
      folders
        .filter(folder => (folder.parentId ?? null) === parentId && !placed.has(folder.id))
        .map(folder => {
          placed.add(folder.id);
          return {
            label: folder.file.fileName,
            href: `/company/documents/folder/${folder.id}`,
            icon: FolderIcon,
            children: buildFolderHierarchy(folder.id),
          };
        });

    const rootChildren = buildFolderHierarchy(null);

    // Surface folders unreachable from the root (a missing parent, or a leftover cycle from before
    // move validation) at the top level so they can never vanish from the tree.
    const orphans = folders
      .filter(folder => !placed.has(folder.id))
      .map(folder => {
        placed.add(folder.id);
        return {
          label: folder.file.fileName,
          href: `/company/documents/folder/${folder.id}`,
          icon: FolderIcon,
          children: buildFolderHierarchy(folder.id),
        };
      });

    return {
      label: 'All company documents',
      href: '/company/documents',
      icon: FolderIcon,
      children: [...rootChildren, ...orphans],
      defaultExpanded: true,
    };
  }, [files]);

  const BackIcon = (props: { size?: number; color?: string }) => <IconArrowLeft {...props} />;
  const sidebarLinks: SidebarItem[] = [
    { label: 'Back to company', href: '/company/funds', icon: BackIcon },
    DIVIDER,
    folderLinks,
  ];

  return (
    <FileDragDropProvider contextValue={dragDropHook}>
      <PageWithSidebar links={sidebarLinks} activeUrl={location.pathname}>
        <Paper variant="content">
          <Stack gap="xxl">
            <PageHeader
              title={title}
              showSearch={showSearch}
              searchValue={searchValue}
              onSearchChange={onSearchChange}
              searchResultCount={searchResultCount}
              actions={actions}
            />
            {children}
          </Stack>
        </Paper>
      </PageWithSidebar>
    </FileDragDropProvider>
  );
}
